/**
 * Секция «вид оборудования» на карточке проекта (задача 89 §3.2) — общая для
 * визарда создания и drawer'а параметров.
 *
 * Ось «структура отчёта» ортогональна шаблону: тип задаёт адресный список
 * элементов сборки. После выбора типа «да»-элементы (обязательные) всегда входят
 * в состав — показаны отмеченными и заблокированы (снять нельзя, §3.2); по
 * «опциям» — явный выбор «Да | Нет» без дефолтов (`<SegmentedStatus>`,
 * `patterns.md §StatusSegmentedControl`) — пропустить молча нельзя. Менеджер
 * проекта только выбирает из каталога; завести недостающий элемент может лишь
 * технолог (эскалация подсказкой).
 *
 * Состояние набора живёт внутри `ElementsChecklist` и репортится наверх через
 * `onSelectionChange`; смена типа сбрасывает набор (`key={typeId}` → remount).
 */
import { Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/ui/combobox";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { Input } from "@/components/ui/input";
import { SegmentedStatus } from "@/components/ui/segmented-status";
import { FormFieldError, type FormValidationIssue } from "@/components/ui/form-validation";
import {
  ProjectMetricsSection,
  type MetricsResult,
} from "@/components/projects/ProjectMetricsSection";
import { useEquipmentTypes, useTypeElements } from "@/hooks/useEquipment";
import type { MetricWithOptions } from "@/api/equipment";

export interface EquipmentSelectionResult {
  /** Итоговый адресный список: все обязательные + опции со статусом «Да». */
  elementIds: number[];
  /** Все опции получили явный ответ «Да»/«Нет» (без «пропустить молча»). */
  allOptionsDecided: boolean;
  /** Хотя бы по одной опции сделан явный выбор «Да»/«Нет». */
  hasOptionalDecisions: boolean;
  firstUndecidedControlId?: string;
}

interface Props {
  typeId: number | null;
  quantity: string;
  onTypeChange: (typeId: number | null) => void;
  onQuantityChange: (quantity: string) => void;
  /**
   * Сид адресного списка при открытии сохранённого проекта (drawer). Пусто/`[]`
   * → свежий выбор: обязательные включены, опции не решены.
   */
  initialElementIds?: number[];
  onSelectionChange: (result: EquipmentSelectionResult) => void;
  disabled?: boolean;
  quantityError?: FormValidationIssue;
  optionsError?: FormValidationIssue;
  metricHierarchy?: {
    projectId: number;
    metrics: MetricWithOptions[];
    onChange: (result: MetricsResult) => void;
  };
}

export function EquipmentTypeSection({
  typeId,
  quantity,
  onTypeChange,
  onQuantityChange,
  initialElementIds,
  onSelectionChange,
  disabled,
  quantityError,
  optionsError,
  metricHierarchy,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const typesQuery = useEquipmentTypes();
  const options = (typesQuery.data ?? [])
    .filter((tp) => tp.id != null)
    .map((tp) => ({ value: String(tp.id), label: tp.name }));

  return (
    <div className="flex flex-col gap-3" data-testid="equipment-type-section">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1 text-sm">
          <span className="font-medium">{t("projects.equipment.typeLabel")}</span>
          <Combobox
            data-testid="equipment-type-select"
            options={options}
            value={typeId != null ? String(typeId) : null}
            onChange={(v) => {
              const nextTypeId = v === null ? null : Number(v);
              onTypeChange(nextTypeId);
            }}
            placeholder={t("projects.equipment.typePlaceholder")}
            searchPlaceholder={t("common.combobox.searchPlaceholder")}
            emptyText={t("common.combobox.empty")}
            clearLabel={t("projects.equipment.typeClear")}
            disabled={disabled}
          />
        </label>
        {typeId != null && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t("projects.equipment.quantityLabel")}</span>
            <Input
              id="equipment-quantity-input"
              type="number"
              min={1}
              step={1}
              value={quantity}
              onChange={(event) => onQuantityChange(event.target.value)}
              disabled={disabled}
              data-testid="equipment-quantity-input"
              aria-invalid={Boolean(quantityError)}
              aria-describedby={quantityError?.id}
            />
            <FormFieldError issue={quantityError} />
          </label>
        )}
      </div>

      <ElementsChecklist
        key={typeId ?? "none"}
        typeId={typeId}
        initialElementIds={initialElementIds}
        onSelectionChange={onSelectionChange}
        metricHierarchy={metricHierarchy}
        optionsError={optionsError}
      />

      {typeId != null && (
        <div className="text-muted-foreground text-xs" data-testid="equipment-escalation-hint">
          {t("projects.equipment.noElementHint")}
        </div>
      )}
    </div>
  );
}

interface ChecklistProps {
  typeId: number | null;
  initialElementIds?: number[];
  onSelectionChange: (result: EquipmentSelectionResult) => void;
  metricHierarchy?: Props["metricHierarchy"];
  optionsError?: FormValidationIssue;
}

function ElementsChecklist({
  typeId,
  initialElementIds,
  onSelectionChange,
  metricHierarchy,
  optionsError,
}: ChecklistProps): React.ReactElement | null {
  const { t } = useTranslation();
  const query = useTypeElements(typeId);
  const data = query.data;

  // Обязательные («да») элементы всегда в составе — снять нельзя (§3.2), поэтому
  // локального состояния по ним нет. optionalDecision: elementId → выбор
  // «Да»(true)/«Нет»(false); отсутствие ключа = не решено (без дефолта).
  const [optionalDecision, setOptionalDecision] = useState<Record<number, boolean>>({});
  const seededRef = useRef(false);
  const firstUndecidedControlRef = useRef<HTMLDivElement>(null);

  // Сид выбора опций из сохранённого набора — один раз, когда резолв загрузился
  // (drawer). Обязательные не сидируем: они всегда включены.
  useEffect(() => {
    if (!data || seededRef.current) return;
    seededRef.current = true;
    if (!initialElementIds || initialElementIds.length === 0) return;
    const chosen = new Set(initialElementIds);
    const opt: Record<number, boolean> = {};
    for (const el of data.optional ?? []) if (el.id != null) opt[el.id] = chosen.has(el.id);
    // Server-data sync: сид выбора опций из сохранённого списка после загрузки
    // резолва (один раз, seededRef). Прямой setState в effect намеренный.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOptionalDecision(opt);
  }, [data, initialElementIds]);

  // Репорт наверх — из resolution + локального состояния. Все зависимости
  // стабильны между несвязанными рендерами, поэтому цикла нет.
  useEffect(() => {
    if (typeId == null) {
      onSelectionChange({
        elementIds: [],
        allOptionsDecided: true,
        hasOptionalDecisions: false,
      });
      return;
    }
    if (query.isLoading) {
      onSelectionChange({
        elementIds: [],
        allOptionsDecided: false,
        hasOptionalDecisions: false,
      });
      return;
    }
    if (query.isError || !data) {
      onSelectionChange({
        elementIds: [],
        allOptionsDecided: true,
        hasOptionalDecisions: false,
      });
      return;
    }
    const req = (data.required ?? []).filter((el) => el.id != null).map((el) => el.id as number);
    const opt = (data.optional ?? [])
      .filter((el) => el.id != null && optionalDecision[el.id] === true)
      .map((el) => el.id as number);
    const allDecided = (data.optional ?? []).every(
      (el) => el.id == null || optionalDecision[el.id] !== undefined,
    );
    const firstUndecidedId = (data.optional ?? []).find(
      (element) => element.id != null && optionalDecision[element.id] === undefined,
    )?.id;
    onSelectionChange({
      elementIds: [...req, ...opt],
      allOptionsDecided: allDecided,
      hasOptionalDecisions: Object.keys(optionalDecision).length > 0,
      ...(firstUndecidedId != null
        ? { firstUndecidedControlId: `equipment-optional-${firstUndecidedId}-yes` }
        : {}),
    });
  }, [typeId, query.isLoading, query.isError, data, optionalDecision, onSelectionChange]);

  if (typeId == null) return null;
  if (query.isLoading) {
    return <div className="text-muted-foreground text-xs">{t("projects.equipment.loading")}</div>;
  }
  if (query.isError || !data) {
    return (
      <ApiErrorNotice
        error={query.error}
        fallbackMessage={t("common.apiError.fallback")}
        className="text-destructive text-xs"
      />
    );
  }

  const required = data.required ?? [];
  const optional = data.optional ?? [];
  if (required.length === 0 && optional.length === 0) {
    return (
      <div className="text-muted-foreground text-xs" data-testid="equipment-elements-empty">
        {t("projects.equipment.emptyElements")}
      </div>
    );
  }

  const undecided = optional.some((el) => el.id != null && optionalDecision[el.id] === undefined);
  const firstUndecidedId = optional.find(
    (element) => element.id != null && optionalDecision[element.id] === undefined,
  )?.id;
  const requiredIds = new Set(required.flatMap((element) => element.id ?? []));
  const selectedIds = [
    ...required.flatMap((element) => element.id ?? []),
    ...optional
      .filter((element) => element.id != null && optionalDecision[element.id] === true)
      .map((element) => element.id as number),
  ];

  if (metricHierarchy) {
    return (
      <div className="flex min-w-0 flex-col gap-3" data-testid="equipment-elements">
        <ProjectMetricsSection
          projectId={metricHierarchy.projectId}
          metrics={metricHierarchy.metrics}
          elements={[...required, ...optional]}
          activeElementIds={selectedIds}
          showEmptyElements
          onChange={metricHierarchy.onChange}
          renderElementHeader={(element) => {
            const isRequired = element.id != null && requiredIds.has(element.id);
            if (isRequired) {
              return (
                <label className="flex min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid={`equipment-required-${element.id}`}
                    checked
                    disabled
                    readOnly
                    className="border-input h-4 w-4 rounded border accent-current"
                  />
                  <span className="min-w-0 flex-1 break-words">{element.name}</span>
                  <Lock
                    className="text-muted-foreground h-3 w-3 shrink-0"
                    aria-label={t("projects.equipment.requiredLockHint")}
                  />
                </label>
              );
            }
            const decision =
              element.id == null || optionalDecision[element.id] === undefined
                ? null
                : optionalDecision[element.id]
                  ? "yes"
                  : "no";
            const optionIssue =
              optionsError && element.id === firstUndecidedId ? optionsError : undefined;
            return (
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <span className="min-w-0 flex-1 break-words">{element.name}</span>
                <SegmentedStatus<"yes" | "no">
                  value={decision}
                  ariaLabel={t("projects.equipment.optionAria", { name: element.name })}
                  data-testid={`equipment-optional-${element.id}`}
                  id={optionIssue?.controlId}
                  ref={optionIssue ? firstUndecidedControlRef : undefined}
                  aria-invalid={optionIssue ? true : undefined}
                  aria-describedby={optionIssue?.id}
                  options={[
                    { value: "yes", label: t("projects.equipment.yes"), tone: "tone-success" },
                    { value: "no", label: t("projects.equipment.no"), tone: "tone-neutral" },
                  ]}
                  onChange={(next) => {
                    if (element.id != null) {
                      setOptionalDecision((state) => ({
                        ...state,
                        [element.id as number]: next === "yes",
                      }));
                    }
                  }}
                />
              </div>
            );
          }}
        />
        {undecided && (
          <div
            className="tone-caution rounded-[var(--radius-chip)] px-2 py-1 text-xs"
            data-testid="equipment-optional-undecided"
          >
            {t("projects.equipment.optionUndecidedHint")}
          </div>
        )}
        <FormFieldError issue={optionsError} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="equipment-elements">
      {required.length > 0 && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("projects.equipment.requiredTitle")}
          </legend>
          <ul className="flex flex-col gap-1" data-testid="equipment-required-list">
            {required.map((el) => (
              <li key={el.id}>
                {/* Обязательный элемент всегда в составе — галочка отмечена и
                    заблокирована (§3.2), снять нельзя. */}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`equipment-required-${el.id}`}
                    checked
                    disabled
                    readOnly
                    className="border-input h-4 w-4 rounded border accent-current"
                  />
                  <span className="min-w-0 flex-1">{el.name}</span>
                  <Lock
                    className="text-muted-foreground h-3 w-3 shrink-0"
                    aria-label={t("projects.equipment.requiredLockHint")}
                  />
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {optional.length > 0 && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("projects.equipment.optionalTitle")}
          </legend>
          <ul className="flex max-w-md flex-col gap-1.5" data-testid="equipment-optional-list">
            {optional.map((el) => {
              const optionIssue =
                optionsError && el.id === firstUndecidedId ? optionsError : undefined;
              return (
                <li key={el.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 flex-1">{el.name}</span>
                  <SegmentedStatus<"yes" | "no">
                    value={
                      el.id == null || optionalDecision[el.id] === undefined
                        ? null
                        : optionalDecision[el.id]
                          ? "yes"
                          : "no"
                    }
                    ariaLabel={t("projects.equipment.optionAria", { name: el.name })}
                    data-testid={`equipment-optional-${el.id}`}
                    id={optionIssue?.controlId}
                    ref={optionIssue ? firstUndecidedControlRef : undefined}
                    aria-invalid={optionIssue ? true : undefined}
                    aria-describedby={optionIssue?.id}
                    options={[
                      { value: "yes", label: t("projects.equipment.yes"), tone: "tone-success" },
                      { value: "no", label: t("projects.equipment.no"), tone: "tone-neutral" },
                    ]}
                    onChange={(next) => {
                      if (el.id != null) {
                        setOptionalDecision((s) => ({ ...s, [el.id as number]: next === "yes" }));
                      }
                    }}
                  />
                </li>
              );
            })}
          </ul>
          {undecided && (
            <div
              className="tone-caution rounded-[var(--radius-chip)] px-2 py-1 text-xs"
              data-testid="equipment-optional-undecided"
            >
              {t("projects.equipment.optionUndecidedHint")}
            </div>
          )}
          <FormFieldError issue={optionsError} />
        </fieldset>
      )}
    </div>
  );
}
